/**
 * SOLID Review
 * S — Good. Presentation of the consent choice: the first-party preferences modal, bound to the factory's `ConsentModalController`
 *     (frontend-framework-oo P3), and the "Cookie settings" button (rendered by the screen that wants it — see CookieSettingsButton).
 *     Booting the CMP, keeping its state and the audit record are the Application's since strict-ports FE4b; the modal's state and
 *     the openers the screens call are the controller's since P3.
 * O — Good. A new entry point calls the controller's opener; a new category is the modal's and the consent schema's, not this file's.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Two public surfaces: the provider, and the `CookieSettingsButton` a screen places where it wants it.
 * D — Good. The controller through the one binding, and the factory for the button's opener; the modal.
 */
import React from 'react';
import { useViewModel } from '../viewmodels/useViewModel';
import { useViewModelFactory } from '../viewmodels/ViewModelProvider';
import { CookiePreferencesModal } from './CookiePreferencesModal';

/**
 * THE COOKIE-CONSENT UI (frontend strict-ports plan, FE4b, 2026-10-04) — the view of `ConsentModalController` since
 * frontend-framework-oo P3 (§28.295). Until FE4b this file was also the CMP's driver (it subscribed to the gateway, wrote a consent
 * store, kept the last recorded signature in a module variable and posted the audit record itself); since FE4b the Application boots
 * the CMP (`startConsent`), keeps the session's consent and asks for the audit record. Until P3 this file still held the modal's
 * state and two module variables — the mounted provider's openers, which the legal footers and the "Cookie settings" button called;
 * they are the controller's now, one per factory, attached while this provider shows.
 *
 * The provider renders inside `ComponentFrameworkProvider` (it reads the client view; flagged in §28.201).
 */
export const CookieConsentProvider: React.FC = () => {
  const [{ prefsOpen, categories }, consent] = useViewModel((views) => views.consent);
  return (
    <>
      <CookiePreferencesModal
        isOpen={prefsOpen}
        initial={categories}
        onSave={consent.save}
        onClose={consent.close}
      />
    </>
  );
};

/**
 * The "Cookie settings" control. It is rendered IN THE FLOW of the screen that places it (the welcome
 * screen's footer row): until 2026-10-07 it was a `position: fixed` pill pinned to the bottom-left of the
 * viewport, which on a phone sat on top of the footer text — and AdSense reviewers flag overlapping
 * content. The provider keeps the modal; this opens it, through the factory's consent controller (P3). A no-op while no provider is
 * mounted.
 */
export const CookieSettingsButton: React.FC = () => {
  const { consent } = useViewModelFactory();
  return (
    <button
      type="button"
      onClick={consent.openModal}
      data-testid="cookie-settings-button"
      aria-label="Cookie settings"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: 36,
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid rgba(255,255,255,0.18)',
        background: 'rgba(0,0,0,0.55)',
        color: 'rgba(255,255,255,0.75)',
        fontSize: 12,
        fontFamily: 'system-ui, sans-serif',
        letterSpacing: '0.04em',
        cursor: 'pointer',
      }}
    >
      {/* A cookie with a bite out of it and four chips — drawn here, so there is no icon library to ship. */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 12.6A9 9 0 1 1 11.4 3 4.6 4.6 0 0 0 16 7.6a4.6 4.6 0 0 0 5 5z" />
        <circle cx="8.5" cy="9" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="13" cy="14" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="8" cy="15" r="0.9" fill="currentColor" stroke="none" />
        <circle cx="15.5" cy="17.5" r="0.9" fill="currentColor" stroke="none" />
      </svg>
      <span>Cookie settings</span>
    </button>
  );
};
